import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  DataGrid,
  Column,
  Editing,
  Toolbar,
  Item,
  FilterRow,
  GroupPanel,
  Grouping,
  SearchPanel,
  DataGridTypes,
  Export,
  Pager,
  Paging,
  Selection,
  DataGridRef,
  StateStoring,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from 'file-saver-es';


const pageSizes = [10, 25, 50, 100];
const storageKey: any = "datagrid-state";

// Fungsi deteksi tipe data
const detectFieldType = (value: any) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";

  if (typeof value === "string") {
    // Cek dengan regex untuk format ISO date string
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDateRegex.test(value)) return "date";
  }

  return "string";
};

// Fungsi parsing date string
const parseIfDateString = (value: any) => {
  if (typeof value === "string") {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDateRegex.test(value)) {
      return new Date(value);
    }
  }
  return value;
};

// Render tag jadi React element
const renderTag = (html: string) => {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

type DynamicDataGridProps = {
  data: any[];
  title: string;
  hiddenColumns?: string[];
  onEditRow?: (rowData: any) => void;
  onDeleteRow?: (rowData: any) => void;
  handleSelection?: any;
};

const DynamicDataGrid: React.FC<DynamicDataGridProps> = ({
  data,
  title,
  hiddenColumns = [],
  onEditRow,
  onDeleteRow,
  handleSelection,
}) => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [stateGrid, setStateGrid] = useState(null);

  const dataGrid = useRef<DataGridRef>(null);

  useEffect(() => {
    if (Array.isArray(data)) {
      setDataSource(data);
    }
  }, [data]);

  const handleDelete = (rowData: any) => {
    if (onDeleteRow) {
      onDeleteRow(rowData);
    } else {
      const filtered = dataSource.filter((item) => item.id_hidden !== rowData.id_hidden);
      setDataSource(filtered);
    }
  };


  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: any) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${title}.xlsx`);
      });
    });
  };

  return (
    <DataGrid
      ref={dataGrid}
      dataSource={dataSource}
      keyExpr="id_hidden"
      showBorders
      columnAutoWidth
      allowColumnReordering
      repaintChangesOnly
      rowAlternationEnabled={false}
      onSelectionChanged={handleSelection}
      onExporting={onExporting}
      onEditingStart={(e) => {
        e.cancel = true;
        if (onEditRow) onEditRow(e.data);
      }}
    >
      <FilterRow visible={false} />
      <GroupPanel visible={true} />
      <Grouping autoExpandAll={true} />
      <SearchPanel visible={true} highlightCaseSensitive={true} />
      <Pager
        visible={true}
        allowedPageSizes={pageSizes}
        showPageSizeSelector={true}
      />
      <Paging defaultPageSize={10} />
      <Export key="id" enabled={true} allowExportSelectedData={true} />

      {/* <Toolbar>
        <Item name="exportButton" />
        <Item name="searchPanel" />
      </Toolbar> */}

      {/* Kolom dinamis */}
      {dataSource.length > 0 &&
        typeof dataSource[0] === "object" &&
        !Array.isArray(dataSource[0]) &&
        Object.keys(dataSource[0])
          .filter((field) => !field.toLowerCase().includes("_hidden"))
          .map((field) => {
            const value = dataSource[0][field];
            const dataType = detectFieldType(value);

            return (
              <Column
                key={field}
                dataField={field}
                dataType={dataType}
                caption={field.replace(/_/g, " ").toUpperCase()}
                cellRender={({ value }) => {
                  if (typeof value === "string" && value.includes("<")) {
                    return renderTag(value);
                  }
                  if (value instanceof Date)
                    return <span>{value.toLocaleDateString()}</span>;
                  return <span>{String(value)}</span>;
                }}
              />
            );
          })}

      {/* Kolom Actions */}
      <Column
        dataField="actions"
        caption="ACTIONS"
        fixed
        fixedPosition="left"
        cellRender={({ data }) => (
          <div style={{ display: "flex", gap: 4 }}>
            <Button
              icon="edit"
              stylingMode="text"
              hint="Edit"
              onClick={() => onEditRow?.(data)}
            />
            <Button
              icon="trash"
              stylingMode="text"
              hint="Hapus"
              onClick={() => handleDelete(data)}
            />
          </div>
        )}
      />

      <Selection
        mode="multiple"
        selectAllMode={"allPages"}
        showCheckBoxesMode={"always"}
      />
    </DataGrid>
  );
};

export default DynamicDataGrid;
